import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { playNotificationSound } from "@/lib/audio";
import { logger } from "@/lib/logger";

const NOTIFICATION_REFRESH_HINT_DELAY_MS = 750;

// TES-1993: Anzeige-Fenster der Liste — Zähl-Quelle sind die count-Queries, nie das Fenster.
const NOTIFICATION_WINDOW_SIZE = 50;

/**
 * Extract a non-PII Postgres/PostgREST signal from a Supabase query error.
 * Only the code/status are surfaced (RLS denials = `42501` / 401 / 403); the
 * error message body is never logged.
 */
const notificationErrorSignal = (
  error: unknown,
): { code: string | null; status: number | null; rlsDenied: boolean } => {
  const e = (error ?? {}) as { code?: unknown; status?: unknown };
  const code = typeof e.code === "string" ? e.code : null;
  const status = typeof e.status === "number" ? e.status : null;
  const rlsDenied = code === "42501" || status === 401 || status === 403;
  return { code, status, rlsDenied };
};

// Request browser notification permission
const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!("Notification" in window)) {
    logger.debug("Browser does not support notifications");
    return "denied";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

/** Timer im Node-Kontext (CI/Vitest) unrefen, damit er den Prozess-Exit nicht blockiert.
 * Zentralisiert den doppelt benötigten unref-Cast (Browser-Timer kennen kein unref). */
const unrefTimer = (timer: ReturnType<typeof setTimeout>) => {
  const t = timer as ReturnType<typeof setTimeout> & { unref?: () => void };
  if (typeof t.unref === "function") t.unref();
};

// Show browser notification when tab is not active
const showBrowserNotification = (title: string, message: string) => {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return; // Only show when tab is hidden

  try {
    const notification = new Notification(title, {
      body: message,
      icon: "/favicon.ico",
      tag: "lovable-notification", // Prevents duplicate notifications
      requireInteraction: false,
    });

    // Auto-close after 5 seconds; unref keeps CI/Vitest process exit unblocked.
    unrefTimer(setTimeout(() => notification.close(), 5000));

    // Focus the tab when notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (e) {
    logger.debug("Could not show browser notification:", e);
  }
};

export type NotificationType =
  | "assignment"
  | "assignment_to_created"
  | "due_reminder"
  | "overdue"
  | "defect_created"
  | "defect_status_changed"
  | "defect_comment"
  | "defect_closed"
  | "inspection_completed"
  // TES-2028: Mig 359 (TES-1871) fuehrte den Enum-Wert serverseitig ein; ohne Union-Mitglied
  // fehlten Icon-/Farb-/Label-Eintraege und die Glocke crashte auf Eskalations-Notifications.
  | "escalation"
  // TES-2053: DB-Trigger (Mig 380/381) schreibt type='inspection_to_verify' (zu-verifizieren-Task).
  | "inspection_to_verify"
  // TES-2082: DB-Trigger (Mig 388/389) schreibt type='owner_restaffing' bei Owner-Deaktivierung.
  | "owner_restaffing";

// Structured payload for localization
export interface NotificationPayload {
  entity_title?: string;
  severity?: string;
  status?: string;
  due_date?: string;
  days_overdue?: number;
  position_name?: string;
  is_first_reminder?: boolean;
  assignee_name?: string;
}

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  entity_type: string | null;
  entity_id: string | null;
  payload?: NotificationPayload | null;
  is_read: boolean;
  email_sent: boolean;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newNotificationIds, setNewNotificationIds] = useState<Set<string>>(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  // TES-1993: Gesamtzahl aus einer dedizierten count-Query — die Liste ist nur das
  // 50er-Anzeige-Fenster und darf nie die Zähl-Quelle sein.
  const [totalCount, setTotalCount] = useState(0);
  const refreshHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Copilot #773 R2: bekannte Notifications (id → is_read) für die Realtime-Handler.
  // Dedup-/Delta-Entscheidungen leben HIER (top-level im Handler), nicht in den
  // setState-Updatern: StrictMode ruft Updater in Dev doppelt auf — dort enqueute
  // Zähler-Deltas zählten +2 statt +1 (beide Aufrufe sehen dasselbe pre-update prev).
  // Gepflegt bei fetch (Fenster-Snapshot), Realtime-INSERT/-UPDATE und markAsRead/markAllAsRead.
  const seenRef = useRef<Map<string, boolean>>(new Map());

  // Clear the "new" animation status for a notification
  const clearNewStatus = useCallback((notificationId: string) => {
    setNewNotificationIds((prev) => {
      const next = new Set(prev);
      next.delete(notificationId);
      return next;
    });
  }, []);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      seenRef.current = new Map();
      setNotifications([]);
      setUnreadCount(0);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    try {
      // TES-1993: Liste (50er-Anzeige-Fenster) + ECHTE Zähler in einem Rutsch. Vorher wurde
      // unread über das Fenster gefiltert — ab >50 Benachrichtigungen fielen ältere ungelesene
      // unter den Tisch (Glocke und Seite gleich falsch). Index: idx_notifications_user_unread.
      const [listRes, unreadRes, totalRes] = await Promise.all([
        supabase
          .from("notifications")
          .select(
            "id, user_id, type, title, message, entity_type, entity_id, payload, is_read, email_sent, created_at",
          )
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(NOTIFICATION_WINDOW_SIZE),
        supabase
          .from("notifications")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("is_read", false),
        supabase
          .from("notifications")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
      ]);

      if (listRes.error) throw listRes.error;
      if (unreadRes.error) throw unreadRes.error;
      if (totalRes.error) throw totalRes.error;

      // Cast the data to our Notification type
      const typedNotifications = (listRes.data || []) as Notification[];
      // Ref mit dem Fetch-Ergebnis synchronisieren (Copilot #773 R2) — Refetch/Initial-Load
      // bleibt die Autorität; danach dedupen die Realtime-Handler gegen diesen Stand.
      seenRef.current = new Map(typedNotifications.map((n) => [n.id, n.is_read]));
      setNotifications(typedNotifications);
      // Fallback aufs Fenster nur, falls PostgREST wider Erwarten keinen count liefert.
      const unread = unreadRes.count ?? typedNotifications.filter((n) => !n.is_read).length;
      const total = totalRes.count ?? typedNotifications.length;
      setUnreadCount(unread);
      setTotalCount(total);
      // success tally — counts only, no PII
      logger.info("Notifications fetched", {
        total,
        unread,
      });
    } catch (error) {
      logger.error("Error fetching notifications", notificationErrorSignal(error));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Cyclo 5 = fachliche Guards (count-Gate R3, Ref-Sync); vollständig durch
  // useNotifications.test abgedeckt — CRAP flaggt nur via CI-0%-Coverage (Coverage-Timeout).
  // fallow-ignore-next-line complexity
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      // Copilot #773 R3: nur ECHTE unread→read-Flips dekrementieren. Der Update ist auf
      // is_read=false gefiltert und liefert per count:"exact", ob wirklich eine Row
      // geflippt ist — Doppelklick / bereits auf anderem Gerät gelesen / nicht (mehr)
      // sichtbare id ergeben count 0 und lassen den Zähler unangetastet.
      const { error, count } = await supabase
        .from("notifications")
        .update({ is_read: true }, { count: "exact" })
        .eq("id", notificationId)
        .eq("is_read", false);

      if (error) throw error;
      if (count !== 1) return; // kein Flip → kein lokales Delta (Echo/Refetch reconciled Rest)

      // Fenster + Zähler lokal nachziehen (TES-1993: Dekrement wirkt auch für Einträge
      // JENSEITS des 50er-Fensters — der Zähler hängt nicht mehr an der Liste).
      // Ref mitziehen (nur bekannte ids): das Realtime-UPDATE-Echo sieht dann Delta 0.
      if (seenRef.current.has(notificationId)) seenRef.current.set(notificationId, true);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      logger.error("Error marking notification as read", {
        notificationId,
        ...notificationErrorSignal(error),
      });
    }
  }, []);

  // Cyclo 5 = fachliche Guards (user-Gate, gefiltertes Update, Ref-Massen-Sync R2); vollständig
  // durch useNotifications.test abgedeckt — CRAP flaggt nur via CI-0%-Coverage (Coverage-Timeout).
  // fallow-ignore-next-line complexity
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;

      // Ref mitziehen: Realtime-UPDATE-Echos der Massen-Aktion erzeugen kein Doppel-Dekrement.
      for (const id of seenRef.current.keys()) seenRef.current.set(id, true);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      logger.error("Error marking all notifications as read", notificationErrorSignal(error));
    }
  }, [user]);

  // Debounced Server-Reconcile — geteilt von Mutation-Hints und Realtime-Events
  // jenseits des Anzeige-Fensters (mehrfaches Schedulen kollabiert in EINEN Refetch).
  const scheduleRefreshHint = useCallback(() => {
    if (refreshHintTimeoutRef.current) {
      clearTimeout(refreshHintTimeoutRef.current);
    }

    refreshHintTimeoutRef.current = setTimeout(() => {
      refreshHintTimeoutRef.current = null;
      void fetchNotifications();
    }, NOTIFICATION_REFRESH_HINT_DELAY_MS);
  }, [fetchNotifications]);

  // Treat mutation-site events as refresh hints; the server owns notification visibility.
  useEffect(() => {
    window.addEventListener("notification:optimistic-increment", scheduleRefreshHint);
    return () => {
      window.removeEventListener("notification:optimistic-increment", scheduleRefreshHint);
      if (refreshHintTimeoutRef.current) {
        clearTimeout(refreshHintTimeoutRef.current);
        refreshHintTimeoutRef.current = null;
      }
    };
  }, [scheduleRefreshHint]);

  // Subscribe to realtime notifications (INSERT and UPDATE events)
  useEffect(() => {
    if (!user) return;

    fetchNotifications();

    // TES-1123: realtime-js >=2.107 returns the EXISTING channel instance for a
    // reused topic; calling .on() on an already-subscribed channel throws
    // ("cannot add `postgres_changes` callbacks ... after `subscribe()`").
    // NotificationBell is mounted twice (TopAppBar + MobileTopBar), so the topic
    // must be unique per hook instance to keep one subscription per mount.
    const channel = supabase
      .channel(`notifications-changes:${user.id}:${crypto.randomUUID()}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          // TES-1993: Zähler per DELTA statt Fenster-Recount — das 50er-Fenster ist nie
          // die Zähl-Quelle. Copilot #773 R2: Dedup VOR den setStates (seenRef) und die
          // Deltas als getrennte, REINE Updates — im setNotifications-Updater enqueued
          // liefen sie unter StrictMode doppelt (+2 statt +1, beide Aufrufe sahen
          // dasselbe pre-update prev).
          if (seenRef.current.has(newNotification.id)) return;
          seenRef.current.set(newNotification.id, newNotification.is_read);
          // Fenster-Invariante: Prepend hart auf die Fenstergröße trimmen (Copilot #773).
          setNotifications((prev) => [newNotification, ...prev].slice(0, NOTIFICATION_WINDOW_SIZE));
          setTotalCount((t) => t + 1);
          if (!newNotification.is_read) setUnreadCount((u) => u + 1);
          // Mark as "new" for animation
          setNewNotificationIds((prev) => new Set(prev).add(newNotification.id));
          // Play notification sound
          playNotificationSound();
          // Show browser notification if tab is hidden
          showBrowserNotification(newNotification.title, newNotification.message);
          // Auto-clear animation status after 3 seconds; unref hält CI/Vitest-Exit frei.
          unrefTimer(
            setTimeout(() => {
              setNewNotificationIds((prev) => {
                const next = new Set(prev);
                next.delete(newNotification.id);
                return next;
              });
            }, 3000),
          );
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;
          // TES-1993: unread per DELTA gegen den bisher bekannten Zustand (seenRef). Eigene
          // markAsRead-Aufrufe haben Ref + Fenster schon aktualisiert → Delta 0 (kein
          // Doppel-Dekrement). Copilot #773 R2: Delta-Entscheidung top-level statt im
          // setNotifications-Updater — Updater müssen rein bleiben (StrictMode-Doppelaufruf
          // zählte sonst doppelt).
          const prevRead = seenRef.current.get(updatedNotification.id);
          if (prevRead === undefined) {
            // Copilot #773: UPDATE zu einer unbekannten id (anderes Gerät, jenseits des
            // Fensters) — Zähler-Reconcile über den debounced Refresh (Server-Wahrheit
            // statt blindem Delta; der Debounce kollabiert „alle gelesen"-Bursts).
            scheduleRefreshHint();
          } else {
            if (prevRead !== updatedNotification.is_read) {
              setUnreadCount((u) => Math.max(0, u + (updatedNotification.is_read ? -1 : 1)));
            }
            seenRef.current.set(updatedNotification.id, updatedNotification.is_read);
          }
          setNotifications((prev) =>
            prev.map((n) => (n.id === updatedNotification.id ? updatedNotification : n)),
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications, scheduleRefreshHint]);

  return {
    notifications,
    newNotificationIds,
    unreadCount,
    /** TES-1993: echte Gesamtzahl (count-Query) — die Liste ist nur das 50er-Fenster. */
    totalCount,
    loading,
    markAsRead,
    markAllAsRead,
    clearNewStatus,
    refetch: fetchNotifications,
    requestBrowserPermission: requestNotificationPermission,
    browserPermission: typeof Notification !== "undefined" ? Notification.permission : "denied",
  };
}
